import React, { useState, useEffect, useCallback, useRef } from "react";
import styled, { ThemeProvider } from "styled-components";
import { token } from "../../../../../theme";

const BOARD_SIZE = 20;
const INITIAL_SNAKE = [{ x: 10, y: 10 }];
const INITIAL_FOOD = { x: 15, y: 15 };
const INITIAL_DIRECTION = { x: 0, y: -1 };

interface Position {
  x: number;
  y: number;
}

type Direction = Position;

const GameContainer = styled.div`
  padding: 16px;
  background: ${(props) => props.theme.MSBackground};
  display: flex;
  gap: 20px;
  align-items: flex-start;
  min-width: 500px;
`;

const GameBoard = styled.div`
  display: grid;
  grid-template-columns: repeat(${BOARD_SIZE}, 20px);
  grid-template-rows: repeat(${BOARD_SIZE}, 20px);
  gap: 1px;
  background: ${(props) => props.theme.darkShadowColor};
  padding: 4px;
  box-shadow: ${(props) => props.theme.invertedStandardBoxShadow};
  border: 2px solid ${(props) => props.theme.darkShadowColor};
`;

const Cell = styled.div<{ type: "empty" | "snake" | "food" | "head" }>`
  width: 20px;
  height: 20px;
  background: ${(props) => {
    switch (props.type) {
      case "snake":
        return "#00aa00";
      case "head":
        return "#00ff00";
      case "food":
        return "#ff0000";
      default:
        return props.theme.MSBackground;
    }
  }};
  border: ${(props) => {
    switch (props.type) {
      case "snake":
      case "head":
        return "1px solid #ffffff";
      case "food":
        return "1px solid #ffaaaa";
      default:
        return "none";
    }
  }};
  box-sizing: border-box;
  ${(props) =>
    props.type === "food" &&
    `
    border-radius: 50%;
  `}
`;

const GameInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 120px;
`;

const ScoreDisplay = styled.div`
  background: ${(props) => props.theme.black};
  color: ${(props) => props.theme.white};
  padding: 8px;
  font-family: monospace;
  font-size: 14px;
  box-shadow: ${(props) => props.theme.invertedStandardBoxShadow};
  text-align: center;
`;

const ControlButton = styled.button`
  background: ${(props) => props.theme.MSBackground};
  box-shadow: ${(props) => props.theme.standardBoxShadow};
  border: none;
  padding: 8px 16px;
  cursor: pointer;
  font-size: ${(props) => props.theme.fontNormal}px;

  &:active {
    box-shadow: ${(props) => props.theme.invertedStandardBoxShadow};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ControlsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  margin-top: 16px;
`;

const DirectionButton = styled(ControlButton)`
  padding: 4px 8px;
  font-size: 12px;
  min-height: 30px;
`;

const ControlsInfo = styled.div`
  font-size: 10px;
  margin-top: 10px;
  line-height: 1.3;
`;

const Snake: React.FC = () => {
  const [snake, setSnake] = useState<Position[]>(INITIAL_SNAKE);
  const [food, setFood] = useState<Position>(INITIAL_FOOD);
  const [direction, setDirection] = useState<Direction>(INITIAL_DIRECTION);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [speed, setSpeed] = useState(200);

  const gameLoopRef = useRef<NodeJS.Timeout | null>(null);
  const directionRef = useRef(direction);

  // Update direction ref when direction changes
  useEffect(() => {
    directionRef.current = direction;
  }, [direction]);

  const generateFood = useCallback((snakeBody: Position[]): Position => {
    let newFood: Position;
    do {
      newFood = {
        x: Math.floor(Math.random() * BOARD_SIZE),
        y: Math.floor(Math.random() * BOARD_SIZE),
      };
    } while (
      snakeBody.some(
        (segment) => segment.x === newFood.x && segment.y === newFood.y
      )
    );
    return newFood;
  }, []);

  const checkCollision = useCallback(
    (head: Position, snakeBody: Position[]): boolean => {
      // Wall collision
      if (
        head.x < 0 ||
        head.x >= BOARD_SIZE ||
        head.y < 0 ||
        head.y >= BOARD_SIZE
      ) {
        return true;
      }

      // Self collision
      return snakeBody.some(
        (segment) => segment.x === head.x && segment.y === head.y
      );
    },
    []
  );

  const moveSnake = useCallback(() => {
    if (gameOver || isPaused || !gameStarted) return;

    setSnake((currentSnake) => {
      const currentDirection = directionRef.current;
      const newSnake = [...currentSnake];
      const head = { ...newSnake[0] };

      head.x += currentDirection.x;
      head.y += currentDirection.y;

      // Check collision
      if (checkCollision(head, newSnake)) {
        setGameOver(true);
        return currentSnake;
      }

      newSnake.unshift(head);

      // Check if food is eaten
      if (head.x === food.x && head.y === food.y) {
        setScore((prev) => prev + 10);
        setFood(generateFood(newSnake));
        // Increase speed slightly
        setSpeed((prev) => Math.max(100, prev - 2));
      } else {
        newSnake.pop();
      }

      return newSnake;
    });
  }, [gameOver, isPaused, gameStarted, food, checkCollision, generateFood]);

  const changeDirection = useCallback(
    (newDirection: Direction) => {
      if (!gameStarted) return;

      // Prevent reverse direction
      const currentDirection = directionRef.current;
      if (
        (newDirection.x === -currentDirection.x &&
          newDirection.y === -currentDirection.y) ||
        (newDirection.x === 0 && newDirection.y === 0)
      ) {
        return;
      }

      setDirection(newDirection);
    },
    [gameStarted]
  );

  const startGame = () => {
    setGameStarted(true);
    setIsPaused(false);
  };

  const resetGame = () => {
    setSnake(INITIAL_SNAKE);
    setFood(INITIAL_FOOD);
    setDirection(INITIAL_DIRECTION);
    setGameOver(false);
    setScore(0);
    setIsPaused(false);
    setGameStarted(false);
    setSpeed(200);
  };

  const togglePause = () => {
    if (!gameStarted) return;
    setIsPaused((prev) => !prev);
  };

  // Game loop
  useEffect(() => {
    if (gameStarted && !gameOver && !isPaused) {
      gameLoopRef.current = setInterval(moveSnake, speed);
    }

    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [moveSnake, speed, gameStarted, gameOver, isPaused]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      e.preventDefault();

      if (!gameStarted && !gameOver) {
        if (e.key === " " || e.key === "Enter") {
          startGame();
          return;
        }
      }

      switch (e.key) {
        case "ArrowUp":
        case "w":
        case "W":
          changeDirection({ x: 0, y: -1 });
          break;
        case "ArrowDown":
        case "s":
        case "S":
          changeDirection({ x: 0, y: 1 });
          break;
        case "ArrowLeft":
        case "a":
        case "A":
          changeDirection({ x: -1, y: 0 });
          break;
        case "ArrowRight":
        case "d":
        case "D":
          changeDirection({ x: 1, y: 0 });
          break;
        case " ":
        case "p":
        case "P":
          togglePause();
          break;
        case "r":
        case "R":
          if (gameOver) {
            resetGame();
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [changeDirection, gameStarted, gameOver]);

  // Render board
  const renderBoard = () => {
    const board = Array(BOARD_SIZE * BOARD_SIZE).fill("empty");

    // Place snake
    snake.forEach((segment, index) => {
      const cellIndex = segment.y * BOARD_SIZE + segment.x;
      if (cellIndex >= 0 && cellIndex < board.length) {
        board[cellIndex] = index === 0 ? "head" : "snake";
      }
    });

    // Place food
    const foodIndex = food.y * BOARD_SIZE + food.x;
    if (foodIndex >= 0 && foodIndex < board.length) {
      board[foodIndex] = "food";
    }

    return board.map((cellType, index) => (
      <Cell
        key={index}
        type={cellType as "empty" | "snake" | "food" | "head"}
      />
    ));
  };

  return (
    <ThemeProvider theme={token}>
      <GameContainer>
        <GameBoard>{renderBoard()}</GameBoard>

        <GameInfo>
          <ScoreDisplay>Score: {score}</ScoreDisplay>

          <ScoreDisplay>Length: {snake.length}</ScoreDisplay>

          {!gameStarted && !gameOver && (
            <ControlButton onClick={startGame}>Start Game</ControlButton>
          )}

          {gameStarted && !gameOver && (
            <ControlButton onClick={togglePause}>
              {isPaused ? "Resume" : "Pause"}
            </ControlButton>
          )}

          <ControlButton onClick={resetGame}>New Game</ControlButton>

          {gameOver && (
            <ScoreDisplay style={{ color: "red" }}>Game Over!</ScoreDisplay>
          )}

          {isPaused && gameStarted && !gameOver && (
            <ScoreDisplay style={{ color: "yellow" }}>Paused</ScoreDisplay>
          )}

          <ControlsContainer>
            <div></div>
            <DirectionButton
              onClick={() => changeDirection({ x: 0, y: -1 })}
              disabled={!gameStarted || gameOver}
            >
              ↑
            </DirectionButton>
            <div></div>

            <DirectionButton
              onClick={() => changeDirection({ x: -1, y: 0 })}
              disabled={!gameStarted || gameOver}
            >
              ←
            </DirectionButton>
            <div></div>
            <DirectionButton
              onClick={() => changeDirection({ x: 1, y: 0 })}
              disabled={!gameStarted || gameOver}
            >
              →
            </DirectionButton>

            <div></div>
            <DirectionButton
              onClick={() => changeDirection({ x: 0, y: 1 })}
              disabled={!gameStarted || gameOver}
            >
              ↓
            </DirectionButton>
            <div></div>
          </ControlsContainer>

          <ControlsInfo>
            <div>
              <strong>Controls:</strong>
            </div>
            <div>Arrow Keys / WASD</div>
            <div>Space/P: Pause</div>
            <div>Enter: Start</div>
            <div>R: Reset (when game over)</div>
          </ControlsInfo>
        </GameInfo>
      </GameContainer>
    </ThemeProvider>
  );
};

export default Snake;
